import { List as ImList, Record as ImRecord, type RecordOf } from 'immutable';

import type { QcsSyncResourceStore, QcsSyncResourceStoreProps } from 'app-components/flux/core/resourceStoreTypes';
import type { transactionsTypes } from 'app-components/flux/transactions';

export const TAXES_REDUCER_KEY = 'taxesStore';
export const TAXES_VERSION = 3;

export type Source = 'YEAR_TO_DATE_REPORT' | 'ANNUAL_ESTIMATE' | 'ANNUAL_USER_ENTERED' | 'UNKNOWN';

export const filingStatuses = [
  'SINGLE',
  'SURVIVING_SPOUSE',
  'HEAD_HOUSE',
  'MARRIED_JOINT',
  'MARRIED_SEPARATE',
] as const;
export type FilingStatus = (typeof filingStatuses)[number];

export type ValueBundleProps = {
  value?: number;
  sourceAttributes?: Source;
};
export type ValueBundle = RecordOf<ValueBundleProps>;
export const mkValueBundle = ImRecord<ValueBundleProps>({
  value: undefined,
  sourceAttributes: undefined,
});

export type WagesProps = {
  selfWages?: ValueBundle | ValueBundleProps;
  selfWagesOther?: ValueBundle | ValueBundleProps;
  spouseWages?: ValueBundle | ValueBundleProps;
  spouseWagesOther?: ValueBundle | ValueBundleProps;
};
export type Wages = RecordOf<WagesProps>;
export const WagesRecord = ImRecord<WagesProps>({
  selfWages: undefined,
  selfWagesOther: undefined,
  spouseWages: undefined,
  spouseWagesOther: undefined,
});
export const mkWages = (props?: WagesProps): Wages =>
  WagesRecord({
    ...props,
    ...(props?.selfWages && {
      selfWages: ImRecord.isRecord(props.selfWages) ? props.selfWages : mkValueBundle(props.selfWages),
    }),
    ...(props?.selfWagesOther && {
      selfWagesOther: ImRecord.isRecord(props.selfWagesOther)
        ? props.selfWagesOther
        : mkValueBundle(props.selfWagesOther),
    }),
    ...(props?.spouseWages && {
      spouseWages: ImRecord.isRecord(props.spouseWages) ? props.spouseWages : mkValueBundle(props.spouseWages),
    }),
    ...(props?.spouseWagesOther && {
      spouseWagesOther: ImRecord.isRecord(props.spouseWagesOther)
        ? props.spouseWagesOther
        : mkValueBundle(props.spouseWagesOther),
    }),
  });

export type TaxPaymentsProps = {
  estimatedTaxesPaidOrPlannedApril?: ValueBundle | ValueBundleProps;
  estimatedTaxesPaidOrPlannedJune?: ValueBundle | ValueBundleProps;
  estimatedTaxesPaidOrPlannedSeptember?: ValueBundle | ValueBundleProps;
  estimatedTaxesPaidOrPlannedJanuary?: ValueBundle | ValueBundleProps;
  refundAppliedFromPriorYearFederalReturn?: ValueBundle | ValueBundleProps;
  estTaxAtLowerPercent?: boolean;
};
export type TaxPayments = RecordOf<TaxPaymentsProps>;
export const TaxPaymentsRecord = ImRecord<TaxPaymentsProps>({
  estimatedTaxesPaidOrPlannedApril: undefined,
  estimatedTaxesPaidOrPlannedJune: undefined,
  estimatedTaxesPaidOrPlannedSeptember: undefined,
  estimatedTaxesPaidOrPlannedJanuary: undefined,
  refundAppliedFromPriorYearFederalReturn: undefined,
  estTaxAtLowerPercent: undefined,
});
export const mkTaxPayments = (props?: TaxPaymentsProps): TaxPayments =>
  TaxPaymentsRecord({
    ...props,
    ...(props?.estimatedTaxesPaidOrPlannedApril && {
      estimatedTaxesPaidOrPlannedApril: ImRecord.isRecord(props.estimatedTaxesPaidOrPlannedApril)
        ? props.estimatedTaxesPaidOrPlannedApril
        : mkValueBundle(props.estimatedTaxesPaidOrPlannedApril),
    }),
    ...(props?.estimatedTaxesPaidOrPlannedJune && {
      estimatedTaxesPaidOrPlannedJune: ImRecord.isRecord(props.estimatedTaxesPaidOrPlannedJune)
        ? props.estimatedTaxesPaidOrPlannedJune
        : mkValueBundle(props.estimatedTaxesPaidOrPlannedJune),
    }),
    ...(props?.estimatedTaxesPaidOrPlannedSeptember && {
      estimatedTaxesPaidOrPlannedSeptember: ImRecord.isRecord(props.estimatedTaxesPaidOrPlannedSeptember)
        ? props.estimatedTaxesPaidOrPlannedSeptember
        : mkValueBundle(props.estimatedTaxesPaidOrPlannedSeptember),
    }),
    ...(props?.estimatedTaxesPaidOrPlannedJanuary && {
      estimatedTaxesPaidOrPlannedJanuary: ImRecord.isRecord(props.estimatedTaxesPaidOrPlannedJanuary)
        ? props.estimatedTaxesPaidOrPlannedJanuary
        : mkValueBundle(props.estimatedTaxesPaidOrPlannedJanuary),
    }),
    ...(props?.refundAppliedFromPriorYearFederalReturn && {
      refundAppliedFromPriorYearFederalReturn: ImRecord.isRecord(props.refundAppliedFromPriorYearFederalReturn)
        ? props.refundAppliedFromPriorYearFederalReturn
        : mkValueBundle(props.refundAppliedFromPriorYearFederalReturn),
    }),
  });

export type DivInterestProps = {
  taxableInterest?: ValueBundle | ValueBundleProps;
  dividends?: ValueBundle | ValueBundleProps;
  qualifiedDividends?: ValueBundle | ValueBundleProps;
};
export type DivInterest = RecordOf<DivInterestProps>;
export const DivInterestRecord = ImRecord<DivInterestProps>({
  taxableInterest: undefined,
  dividends: undefined,
  qualifiedDividends: undefined,
});
export const mkDivInterest = (props?: DivInterestProps): DivInterest =>
  DivInterestRecord({
    ...props,
    ...(props?.taxableInterest && {
      taxableInterest: ImRecord.isRecord(props.taxableInterest)
        ? props.taxableInterest
        : mkValueBundle(props.taxableInterest),
    }),
    ...(props?.dividends && {
      dividends: ImRecord.isRecord(props.dividends) ? props.dividends : mkValueBundle(props.dividends),
    }),
    ...(props?.qualifiedDividends && {
      qualifiedDividends: ImRecord.isRecord(props.qualifiedDividends)
        ? props.qualifiedDividends
        : mkValueBundle(props.qualifiedDividends),
    }),
  });

export type CapitalGainsProps = {
  shortTermGains?: ValueBundle | ValueBundleProps;
  unrecapturedDepreciationGains?: ValueBundle | ValueBundleProps;
  longTerm28PctGains?: ValueBundle | ValueBundleProps;
  longTermGains?: ValueBundle | ValueBundleProps;
  shortTermCarryovers?: ValueBundle | ValueBundleProps;
  longTermCarryovers?: ValueBundle | ValueBundleProps;
  altMinDifferent?: boolean;
  shortTermGainsAltMin?: ValueBundle | ValueBundleProps;
  unrecapturedDepreciationGainsAltMin?: ValueBundle | ValueBundleProps;
  longTerm28PctGainsAltMin?: ValueBundle | ValueBundleProps;
};
export type CapitalGains = RecordOf<CapitalGainsProps>;
export const CapitalGainsRecord = ImRecord<CapitalGainsProps>({
  shortTermGains: undefined,
  unrecapturedDepreciationGains: undefined,
  longTerm28PctGains: undefined,
  longTermGains: undefined,
  shortTermCarryovers: undefined,
  longTermCarryovers: undefined,
  altMinDifferent: undefined,
  shortTermGainsAltMin: undefined,
  unrecapturedDepreciationGainsAltMin: undefined,
  longTerm28PctGainsAltMin: undefined,
});
export const mkCapitalGains = (props?: CapitalGainsProps): CapitalGains =>
  CapitalGainsRecord({
    ...props,
    ...(props?.shortTermGains && {
      shortTermGains: ImRecord.isRecord(props.shortTermGains)
        ? props.shortTermGains
        : mkValueBundle(props.shortTermGains),
    }),
    ...(props?.unrecapturedDepreciationGains && {
      unrecapturedDepreciationGains: ImRecord.isRecord(props.unrecapturedDepreciationGains)
        ? props.unrecapturedDepreciationGains
        : mkValueBundle(props.unrecapturedDepreciationGains),
    }),
    ...(props?.longTerm28PctGains && {
      longTerm28PctGains: ImRecord.isRecord(props.longTerm28PctGains)
        ? props.longTerm28PctGains
        : mkValueBundle(props.longTerm28PctGains),
    }),
    ...(props?.longTermGains && {
      longTermGains: ImRecord.isRecord(props.longTermGains) ? props.longTermGains : mkValueBundle(props.longTermGains),
    }),
    ...(props?.shortTermCarryovers && {
      shortTermCarryovers: ImRecord.isRecord(props.shortTermCarryovers)
        ? props.shortTermCarryovers
        : mkValueBundle(props.shortTermCarryovers),
    }),
    ...(props?.longTermCarryovers && {
      longTermCarryovers: ImRecord.isRecord(props.longTermCarryovers)
        ? props.longTermCarryovers
        : mkValueBundle(props.longTermCarryovers),
    }),
    ...(props?.altMinDifferent && { altMinDifferent: props.altMinDifferent }),
    ...(props?.shortTermGainsAltMin && {
      shortTermGainsAltMin: ImRecord.isRecord(props.shortTermGainsAltMin)
        ? props.shortTermGainsAltMin
        : mkValueBundle(props.shortTermGainsAltMin),
    }),
    ...(props?.unrecapturedDepreciationGainsAltMin && {
      unrecapturedDepreciationGainsAltMin: ImRecord.isRecord(props.unrecapturedDepreciationGainsAltMin)
        ? props.unrecapturedDepreciationGainsAltMin
        : mkValueBundle(props.unrecapturedDepreciationGainsAltMin),
    }),
    ...(props?.longTerm28PctGainsAltMin && {
      longTerm28PctGainsAltMin: ImRecord.isRecord(props.longTerm28PctGainsAltMin)
        ? props.longTerm28PctGainsAltMin
        : mkValueBundle(props.longTerm28PctGainsAltMin),
    }),
  });

export type BusinessIncomeProps = {
  revenueSelf?: ValueBundle | ValueBundleProps;
  costOfGoodsSelf?: ValueBundle | ValueBundleProps;
  mealsAndEntertainmentSelf?: ValueBundle | ValueBundleProps;
  otherAllowableExpensesSelf?: ValueBundle | ValueBundleProps;
  businessUseOfHomeSelf?: ValueBundle | ValueBundleProps;
  deductibleBusinessMileageSelf?: ValueBundle | ValueBundleProps;
  unspecifiedBusinessExpensesSelf?: ValueBundle | ValueBundleProps;
  revenueSpouse?: ValueBundle | ValueBundleProps;
  costOfGoodsSpouse?: ValueBundle | ValueBundleProps;
  mealsAndEntertainmentSpouse?: ValueBundle | ValueBundleProps;
  otherAllowableExpensesSpouse?: ValueBundle | ValueBundleProps;
  businessUseOfHomeSpouse?: ValueBundle | ValueBundleProps;
  deductibleBusinessMileageSpouse?: ValueBundle | ValueBundleProps;
  unspecifiedBusinessExpensesSpouse?: ValueBundle | ValueBundleProps;
};
export type BusinessIncome = RecordOf<BusinessIncomeProps>;
export const BusinessIncomeRecord = ImRecord<BusinessIncomeProps>({
  revenueSelf: undefined,
  costOfGoodsSelf: undefined,
  mealsAndEntertainmentSelf: undefined,
  otherAllowableExpensesSelf: undefined,
  businessUseOfHomeSelf: undefined,
  deductibleBusinessMileageSelf: undefined,
  unspecifiedBusinessExpensesSelf: undefined,
  revenueSpouse: undefined,
  costOfGoodsSpouse: undefined,
  mealsAndEntertainmentSpouse: undefined,
  otherAllowableExpensesSpouse: undefined,
  businessUseOfHomeSpouse: undefined,
  deductibleBusinessMileageSpouse: undefined,
  unspecifiedBusinessExpensesSpouse: undefined,
});
export const mkBusinessIncome = (props?: BusinessIncomeProps): BusinessIncome =>
  BusinessIncomeRecord({
    ...props,
    ...(props?.revenueSelf && {
      revenueSelf: ImRecord.isRecord(props.revenueSelf) ? props.revenueSelf : mkValueBundle(props.revenueSelf),
    }),
    ...(props?.costOfGoodsSelf && {
      costOfGoodsSelf: ImRecord.isRecord(props.costOfGoodsSelf)
        ? props.costOfGoodsSelf
        : mkValueBundle(props.costOfGoodsSelf),
    }),
    ...(props?.mealsAndEntertainmentSelf && {
      mealsAndEntertainmentSelf: ImRecord.isRecord(props.mealsAndEntertainmentSelf)
        ? props.mealsAndEntertainmentSelf
        : mkValueBundle(props.mealsAndEntertainmentSelf),
    }),
    ...(props?.otherAllowableExpensesSelf && {
      otherAllowableExpensesSelf: ImRecord.isRecord(props.otherAllowableExpensesSelf)
        ? props.otherAllowableExpensesSelf
        : mkValueBundle(props.otherAllowableExpensesSelf),
    }),
    ...(props?.businessUseOfHomeSelf && {
      businessUseOfHomeSelf: ImRecord.isRecord(props.businessUseOfHomeSelf)
        ? props.businessUseOfHomeSelf
        : mkValueBundle(props.businessUseOfHomeSelf),
    }),
    ...(props?.deductibleBusinessMileageSelf && {
      deductibleBusinessMileageSelf: ImRecord.isRecord(props.deductibleBusinessMileageSelf)
        ? props.deductibleBusinessMileageSelf
        : mkValueBundle(props.deductibleBusinessMileageSelf),
    }),
    ...(props?.unspecifiedBusinessExpensesSelf && {
      unspecifiedBusinessExpensesSelf: ImRecord.isRecord(props.unspecifiedBusinessExpensesSelf)
        ? props.unspecifiedBusinessExpensesSelf
        : mkValueBundle(props.unspecifiedBusinessExpensesSelf),
    }),
    ...(props?.revenueSpouse && {
      revenueSpouse: ImRecord.isRecord(props.revenueSpouse) ? props.revenueSpouse : mkValueBundle(props.revenueSpouse),
    }),
    ...(props?.costOfGoodsSpouse && {
      costOfGoodsSpouse: ImRecord.isRecord(props.costOfGoodsSpouse)
        ? props.costOfGoodsSpouse
        : mkValueBundle(props.costOfGoodsSpouse),
    }),
    ...(props?.mealsAndEntertainmentSpouse && {
      mealsAndEntertainmentSpouse: ImRecord.isRecord(props.mealsAndEntertainmentSpouse)
        ? props.mealsAndEntertainmentSpouse
        : mkValueBundle(props.mealsAndEntertainmentSpouse),
    }),
    ...(props?.otherAllowableExpensesSpouse && {
      otherAllowableExpensesSpouse: ImRecord.isRecord(props.otherAllowableExpensesSpouse)
        ? props.otherAllowableExpensesSpouse
        : mkValueBundle(props.otherAllowableExpensesSpouse),
    }),
    ...(props?.businessUseOfHomeSpouse && {
      businessUseOfHomeSpouse: ImRecord.isRecord(props.businessUseOfHomeSpouse)
        ? props.businessUseOfHomeSpouse
        : mkValueBundle(props.businessUseOfHomeSpouse),
    }),
    ...(props?.deductibleBusinessMileageSpouse && {
      deductibleBusinessMileageSpouse: ImRecord.isRecord(props.deductibleBusinessMileageSpouse)
        ? props.deductibleBusinessMileageSpouse
        : mkValueBundle(props.deductibleBusinessMileageSpouse),
    }),
    ...(props?.unspecifiedBusinessExpensesSpouse && {
      unspecifiedBusinessExpensesSpouse: ImRecord.isRecord(props.unspecifiedBusinessExpensesSpouse)
        ? props.unspecifiedBusinessExpensesSpouse
        : mkValueBundle(props.unspecifiedBusinessExpensesSpouse),
    }),
  });

export type OtherIncomeProps = {
  taxableRefundOfStateLocalIncomeTax?: ValueBundle | ValueBundleProps;
  alimonyReceived?: ValueBundle | ValueBundleProps;
  taxableIRAPensionDistributions?: ValueBundle | ValueBundleProps;
  unemploymentCompensation?: ValueBundle | ValueBundleProps;
  taxableSocialSecurityBenefits?: ValueBundle | ValueBundleProps;
  otherIncomeGainsOrLosses?: ValueBundle | ValueBundleProps;
  socialSecurityRRAIncome?: ValueBundle | ValueBundleProps;
};
export type OtherIncome = RecordOf<OtherIncomeProps>;
export const OtherIncomeRecord = ImRecord<OtherIncomeProps>({
  taxableRefundOfStateLocalIncomeTax: undefined,
  alimonyReceived: undefined,
  taxableIRAPensionDistributions: undefined,
  unemploymentCompensation: undefined,
  taxableSocialSecurityBenefits: undefined,
  otherIncomeGainsOrLosses: undefined,
  socialSecurityRRAIncome: undefined,
});
export const mkOtherIncome = (props?: OtherIncomeProps): OtherIncome =>
  OtherIncomeRecord({
    ...props,
    ...(props?.taxableRefundOfStateLocalIncomeTax && {
      taxableRefundOfStateLocalIncomeTax: ImRecord.isRecord(props.taxableRefundOfStateLocalIncomeTax)
        ? props.taxableRefundOfStateLocalIncomeTax
        : mkValueBundle(props.taxableRefundOfStateLocalIncomeTax),
    }),
    ...(props?.alimonyReceived && {
      alimonyReceived: ImRecord.isRecord(props.alimonyReceived)
        ? props.alimonyReceived
        : mkValueBundle(props.alimonyReceived),
    }),
    ...(props?.taxableIRAPensionDistributions && {
      taxableIRAPensionDistributions: ImRecord.isRecord(props.taxableIRAPensionDistributions)
        ? props.taxableIRAPensionDistributions
        : mkValueBundle(props.taxableIRAPensionDistributions),
    }),
    ...(props?.unemploymentCompensation && {
      unemploymentCompensation: ImRecord.isRecord(props.unemploymentCompensation)
        ? props.unemploymentCompensation
        : mkValueBundle(props.unemploymentCompensation),
    }),
    ...(props?.taxableSocialSecurityBenefits && {
      taxableSocialSecurityBenefits: ImRecord.isRecord(props.taxableSocialSecurityBenefits)
        ? props.taxableSocialSecurityBenefits
        : mkValueBundle(props.taxableSocialSecurityBenefits),
    }),
    ...(props?.otherIncomeGainsOrLosses && {
      otherIncomeGainsOrLosses: ImRecord.isRecord(props.otherIncomeGainsOrLosses)
        ? props.otherIncomeGainsOrLosses
        : mkValueBundle(props.otherIncomeGainsOrLosses),
    }),
    ...(props?.socialSecurityRRAIncome && {
      socialSecurityRRAIncome: ImRecord.isRecord(props.socialSecurityRRAIncome)
        ? props.socialSecurityRRAIncome
        : mkValueBundle(props.socialSecurityRRAIncome),
    }),
  });

export type ScheduleEProps = {
  partnershipIncome?: ValueBundle | ValueBundleProps;
  rents?: ValueBundle | ValueBundleProps;
  royalties?: ValueBundle | ValueBundleProps;
  depreciation?: ValueBundle | ValueBundleProps;
  rentalPropertyMileage?: ValueBundle | ValueBundleProps;
  expenses?: ValueBundle | ValueBundleProps;
  unspecifiedRentalPropertyExpense?: ValueBundle | ValueBundleProps;
};
export type ScheduleE = RecordOf<ScheduleEProps>;
export const ScheduleERecord = ImRecord<ScheduleEProps>({
  partnershipIncome: undefined,
  rents: undefined,
  royalties: undefined,
  depreciation: undefined,
  rentalPropertyMileage: undefined,
  expenses: undefined,
  unspecifiedRentalPropertyExpense: undefined,
});
export const mkScheduleE = (props?: ScheduleEProps): ScheduleE =>
  ScheduleERecord({
    ...props,
    ...(props?.partnershipIncome && {
      partnershipIncome: ImRecord.isRecord(props.partnershipIncome)
        ? props.partnershipIncome
        : mkValueBundle(props.partnershipIncome),
    }),
    ...(props?.rents && {
      rents: ImRecord.isRecord(props.rents) ? props.rents : mkValueBundle(props.rents),
    }),
    ...(props?.royalties && {
      royalties: ImRecord.isRecord(props.royalties) ? props.royalties : mkValueBundle(props.royalties),
    }),
    ...(props?.depreciation && {
      depreciation: ImRecord.isRecord(props.depreciation) ? props.depreciation : mkValueBundle(props.depreciation),
    }),
    ...(props?.rentalPropertyMileage && {
      rentalPropertyMileage: ImRecord.isRecord(props.rentalPropertyMileage)
        ? props.rentalPropertyMileage
        : mkValueBundle(props.rentalPropertyMileage),
    }),
    ...(props?.expenses && {
      expenses: ImRecord.isRecord(props.expenses) ? props.expenses : mkValueBundle(props.expenses),
    }),
    ...(props?.unspecifiedRentalPropertyExpense && {
      unspecifiedRentalPropertyExpense: ImRecord.isRecord(props.unspecifiedRentalPropertyExpense)
        ? props.unspecifiedRentalPropertyExpense
        : mkValueBundle(props.unspecifiedRentalPropertyExpense),
    }),
  });

export type ScheduleFProps = {
  sales?: ValueBundle | ValueBundleProps;
  supplementalPayments?: ValueBundle | ValueBundleProps;
  othIncome?: ValueBundle | ValueBundleProps;
  expenses?: ValueBundle | ValueBundleProps;
};
export type ScheduleF = RecordOf<ScheduleFProps>;
export const ScheduleFRecord = ImRecord<ScheduleFProps>({
  sales: undefined,
  supplementalPayments: undefined,
  othIncome: undefined,
  expenses: undefined,
});
export const mkScheduleF = (props?: ScheduleFProps): ScheduleF =>
  ScheduleFRecord({
    ...props,
    ...(props?.sales && { sales: ImRecord.isRecord(props.sales) ? props.sales : mkValueBundle(props.sales) }),
    ...(props?.supplementalPayments && {
      supplementalPayments: ImRecord.isRecord(props.supplementalPayments)
        ? props.supplementalPayments
        : mkValueBundle(props.supplementalPayments),
    }),
    ...(props?.othIncome && {
      othIncome: ImRecord.isRecord(props.othIncome) ? props.othIncome : mkValueBundle(props.othIncome),
    }),
    ...(props?.expenses && {
      expenses: ImRecord.isRecord(props.expenses) ? props.expenses : mkValueBundle(props.expenses),
    }),
  });

export type AdjustmentsProps = {
  allowableIRADeduction?: ValueBundle | ValueBundleProps;
  allowableSEHealthInsuranceDeduction?: ValueBundle | ValueBundleProps;
  keoghSEPDeduction?: ValueBundle | ValueBundleProps;
  penaltyOnEarlySavingsWithdrawal?: ValueBundle | ValueBundleProps;
  alimonyPaid?: ValueBundle | ValueBundleProps;
  movingExpenses?: ValueBundle | ValueBundleProps;
  otherAdjustments?: ValueBundle | ValueBundleProps;
};
export type Adjustments = RecordOf<AdjustmentsProps>;
export const AdjustmentsRecord = ImRecord<AdjustmentsProps>({
  allowableIRADeduction: undefined,
  allowableSEHealthInsuranceDeduction: undefined,
  keoghSEPDeduction: undefined,
  penaltyOnEarlySavingsWithdrawal: undefined,
  alimonyPaid: undefined,
  movingExpenses: undefined,
  otherAdjustments: undefined,
});
export const mkAdjustments = (props?: AdjustmentsProps): Adjustments =>
  AdjustmentsRecord({
    ...props,
    ...(props?.allowableIRADeduction && {
      allowableIRADeduction: ImRecord.isRecord(props.allowableIRADeduction)
        ? props.allowableIRADeduction
        : mkValueBundle(props.allowableIRADeduction),
    }),
    ...(props?.allowableSEHealthInsuranceDeduction && {
      allowableSEHealthInsuranceDeduction: ImRecord.isRecord(props.allowableSEHealthInsuranceDeduction)
        ? props.allowableSEHealthInsuranceDeduction
        : mkValueBundle(props.allowableSEHealthInsuranceDeduction),
    }),
    ...(props?.keoghSEPDeduction && {
      keoghSEPDeduction: ImRecord.isRecord(props.keoghSEPDeduction)
        ? props.keoghSEPDeduction
        : mkValueBundle(props.keoghSEPDeduction),
    }),
    ...(props?.penaltyOnEarlySavingsWithdrawal && {
      penaltyOnEarlySavingsWithdrawal: ImRecord.isRecord(props.penaltyOnEarlySavingsWithdrawal)
        ? props.penaltyOnEarlySavingsWithdrawal
        : mkValueBundle(props.penaltyOnEarlySavingsWithdrawal),
    }),
    ...(props?.alimonyPaid && {
      alimonyPaid: ImRecord.isRecord(props.alimonyPaid) ? props.alimonyPaid : mkValueBundle(props.alimonyPaid),
    }),
    ...(props?.movingExpenses && {
      movingExpenses: ImRecord.isRecord(props.movingExpenses)
        ? props.movingExpenses
        : mkValueBundle(props.movingExpenses),
    }),
    ...(props?.otherAdjustments && {
      otherAdjustments: ImRecord.isRecord(props.otherAdjustments)
        ? props.otherAdjustments
        : mkValueBundle(props.otherAdjustments),
    }),
  });

export type ScheduleADeductionsProps = {
  medicalAndDental?: ValueBundle | ValueBundleProps;
  realEstateAndOtherTaxes?: ValueBundle | ValueBundleProps;
  deductibleInvestmentInterest?: ValueBundle | ValueBundleProps;
  allowableMortgageAndOtherDeductibleInterest?: ValueBundle | ValueBundleProps;
  charitableContributions?: ValueBundle | ValueBundleProps;
  deductibleCasualtyLosses?: ValueBundle | ValueBundleProps;
  miscDeductions?: ValueBundle | ValueBundleProps;
  canBeClaimedAsDependent?: boolean;
  bBlindSelf?: boolean;
  bBlindSpouse?: boolean;
  b65OrOlderSelf?: boolean;
  b65OrOlderSpouse?: boolean;
  miscDeductionsNoLimit?: ValueBundle | ValueBundleProps;
};
export type ScheduleADeductions = RecordOf<ScheduleADeductionsProps>;
export const ScheduleADeductionsRecord = ImRecord<ScheduleADeductionsProps>({
  medicalAndDental: undefined,
  realEstateAndOtherTaxes: undefined,
  deductibleInvestmentInterest: undefined,
  allowableMortgageAndOtherDeductibleInterest: undefined,
  charitableContributions: undefined,
  deductibleCasualtyLosses: undefined,
  miscDeductions: undefined,
  canBeClaimedAsDependent: undefined,
  bBlindSelf: undefined,
  bBlindSpouse: undefined,
  b65OrOlderSelf: undefined,
  b65OrOlderSpouse: undefined,
  miscDeductionsNoLimit: undefined,
});
export const mkScheduleADeductions = (props?: ScheduleADeductionsProps): ScheduleADeductions =>
  ScheduleADeductionsRecord({
    ...props,
    ...(props?.medicalAndDental && {
      medicalAndDental: ImRecord.isRecord(props.medicalAndDental)
        ? props.medicalAndDental
        : mkValueBundle(props.medicalAndDental),
    }),
    ...(props?.realEstateAndOtherTaxes && {
      realEstateAndOtherTaxes: ImRecord.isRecord(props.realEstateAndOtherTaxes)
        ? props.realEstateAndOtherTaxes
        : mkValueBundle(props.realEstateAndOtherTaxes),
    }),
    ...(props?.deductibleInvestmentInterest && {
      deductibleInvestmentInterest: ImRecord.isRecord(props.deductibleInvestmentInterest)
        ? props.deductibleInvestmentInterest
        : mkValueBundle(props.deductibleInvestmentInterest),
    }),
    ...(props?.allowableMortgageAndOtherDeductibleInterest && {
      allowableMortgageAndOtherDeductibleInterest: ImRecord.isRecord(props.allowableMortgageAndOtherDeductibleInterest)
        ? props.allowableMortgageAndOtherDeductibleInterest
        : mkValueBundle(props.allowableMortgageAndOtherDeductibleInterest),
    }),
    ...(props?.charitableContributions && {
      charitableContributions: ImRecord.isRecord(props.charitableContributions)
        ? props.charitableContributions
        : mkValueBundle(props.charitableContributions),
    }),
    ...(props?.deductibleCasualtyLosses && {
      deductibleCasualtyLosses: ImRecord.isRecord(props.deductibleCasualtyLosses)
        ? props.deductibleCasualtyLosses
        : mkValueBundle(props.deductibleCasualtyLosses),
    }),
    ...(props?.miscDeductions && {
      miscDeductions: ImRecord.isRecord(props.miscDeductions)
        ? props.miscDeductions
        : mkValueBundle(props.miscDeductions),
    }),
    ...(props?.miscDeductionsNoLimit && {
      miscDeductionsNoLimit: ImRecord.isRecord(props.miscDeductionsNoLimit)
        ? props.miscDeductionsNoLimit
        : mkValueBundle(props.miscDeductionsNoLimit),
    }),
  });

export type StateAndLocalIncomeTaxProps = {
  withholdingsSelf?: ValueBundle | ValueBundleProps;
  withholdingsSpouse?: ValueBundle | ValueBundleProps;
  sNextPayDateSelf?: string;
  sNextPayDateSpouse?: string;
  sPayPeriodSelf?: string;
  sPayPeriodSpouse?: string;
  withholdingPerPayPeriodSelf?: ValueBundle | ValueBundleProps;
  withholdingPerPayPeriodSpouse?: ValueBundle | ValueBundleProps;
  calcProjectedTotalWithholdingSelfAndSpouse?: ValueBundle | ValueBundleProps;
  estimatedTaxesPaidToDatePlusProjected?: ValueBundle | ValueBundleProps;
  taxesPaidThisYearForLastYearsStateTax?: ValueBundle | ValueBundleProps;
};
export type StateAndLocalIncomeTax = RecordOf<StateAndLocalIncomeTaxProps>;
export const StateAndLocalIncomeTaxRecord = ImRecord<StateAndLocalIncomeTaxProps>({
  withholdingsSelf: undefined,
  withholdingsSpouse: undefined,
  sNextPayDateSelf: undefined,
  sNextPayDateSpouse: undefined,
  sPayPeriodSelf: undefined,
  sPayPeriodSpouse: undefined,
  withholdingPerPayPeriodSelf: undefined,
  withholdingPerPayPeriodSpouse: undefined,
  calcProjectedTotalWithholdingSelfAndSpouse: undefined,
  estimatedTaxesPaidToDatePlusProjected: undefined,
  taxesPaidThisYearForLastYearsStateTax: undefined,
});
export const mkStateAndLocalIncomeTax = (props?: StateAndLocalIncomeTaxProps): StateAndLocalIncomeTax =>
  StateAndLocalIncomeTaxRecord({
    ...props,
    ...(props?.withholdingsSelf && {
      withholdingsSelf: ImRecord.isRecord(props.withholdingsSelf)
        ? props.withholdingsSelf
        : mkValueBundle(props.withholdingsSelf),
    }),
    ...(props?.withholdingsSpouse && {
      withholdingsSpouse: ImRecord.isRecord(props.withholdingsSpouse)
        ? props.withholdingsSpouse
        : mkValueBundle(props.withholdingsSpouse),
    }),
    ...(props?.withholdingPerPayPeriodSelf && {
      withholdingPerPayPeriodSelf: ImRecord.isRecord(props.withholdingPerPayPeriodSelf)
        ? props.withholdingPerPayPeriodSelf
        : mkValueBundle(props.withholdingPerPayPeriodSelf),
    }),
    ...(props?.withholdingPerPayPeriodSpouse && {
      withholdingPerPayPeriodSpouse: ImRecord.isRecord(props.withholdingPerPayPeriodSpouse)
        ? props.withholdingPerPayPeriodSpouse
        : mkValueBundle(props.withholdingPerPayPeriodSpouse),
    }),
    ...(props?.calcProjectedTotalWithholdingSelfAndSpouse && {
      calcProjectedTotalWithholdingSelfAndSpouse: ImRecord.isRecord(props.calcProjectedTotalWithholdingSelfAndSpouse)
        ? props.calcProjectedTotalWithholdingSelfAndSpouse
        : mkValueBundle(props.calcProjectedTotalWithholdingSelfAndSpouse),
    }),
    ...(props?.estimatedTaxesPaidToDatePlusProjected && {
      estimatedTaxesPaidToDatePlusProjected: ImRecord.isRecord(props.estimatedTaxesPaidToDatePlusProjected)
        ? props.estimatedTaxesPaidToDatePlusProjected
        : mkValueBundle(props.estimatedTaxesPaidToDatePlusProjected),
    }),
  });

export type TaxesAndCreditsProps = {
  seOtherIncomeSelf?: ValueBundle | ValueBundleProps;
  seOtherIncomeSpouse?: ValueBundle | ValueBundleProps;
  seOtherWagesSelf?: ValueBundle | ValueBundleProps;
  seOtherWagesSpouse?: ValueBundle | ValueBundleProps;
  amtOtherAdjustmentsAndTaxPreferenceItems?: ValueBundle | ValueBundleProps;
  otherTax?: ValueBundle | ValueBundleProps;
  credits?: ValueBundle | ValueBundleProps;
  amtMedicalExpAdjustmentsItems?: ValueBundle | ValueBundleProps;
  bNotSubjectToSETaxSelf?: boolean;
  bNotSubjectToSETaxSpouse?: boolean;
  niiagiAdj?: ValueBundle | ValueBundleProps;
};
export type TaxesAndCredits = RecordOf<TaxesAndCreditsProps>;
export const TaxesAndCreditsRecord = ImRecord<TaxesAndCreditsProps>({
  seOtherIncomeSelf: undefined,
  seOtherIncomeSpouse: undefined,
  seOtherWagesSelf: undefined,
  seOtherWagesSpouse: undefined,
  amtOtherAdjustmentsAndTaxPreferenceItems: undefined,
  otherTax: undefined,
  credits: undefined,
  amtMedicalExpAdjustmentsItems: undefined,
  bNotSubjectToSETaxSelf: undefined,
  bNotSubjectToSETaxSpouse: undefined,
  niiagiAdj: undefined,
});
export const mkTaxesAndCredits = (props?: TaxesAndCreditsProps): TaxesAndCredits =>
  TaxesAndCreditsRecord({
    ...props,
    ...(props?.seOtherIncomeSelf && {
      seOtherIncomeSelf: ImRecord.isRecord(props.seOtherIncomeSelf)
        ? props.seOtherIncomeSelf
        : mkValueBundle(props.seOtherIncomeSelf),
    }),
    ...(props?.seOtherIncomeSpouse && {
      seOtherIncomeSpouse: ImRecord.isRecord(props.seOtherIncomeSpouse)
        ? props.seOtherIncomeSpouse
        : mkValueBundle(props.seOtherIncomeSpouse),
    }),
    ...(props?.seOtherWagesSelf && {
      seOtherWagesSelf: ImRecord.isRecord(props.seOtherWagesSelf)
        ? props.seOtherWagesSelf
        : mkValueBundle(props.seOtherWagesSelf),
    }),
    ...(props?.seOtherWagesSpouse && {
      seOtherWagesSpouse: ImRecord.isRecord(props.seOtherWagesSpouse)
        ? props.seOtherWagesSpouse
        : mkValueBundle(props.seOtherWagesSpouse),
    }),
    ...(props?.amtOtherAdjustmentsAndTaxPreferenceItems && {
      amtOtherAdjustmentsAndTaxPreferenceItems: ImRecord.isRecord(props.amtOtherAdjustmentsAndTaxPreferenceItems)
        ? props.amtOtherAdjustmentsAndTaxPreferenceItems
        : mkValueBundle(props.amtOtherAdjustmentsAndTaxPreferenceItems),
    }),
    ...(props?.otherTax && {
      otherTax: ImRecord.isRecord(props.otherTax) ? props.otherTax : mkValueBundle(props.otherTax),
    }),
    ...(props?.credits && {
      credits: ImRecord.isRecord(props.credits) ? props.credits : mkValueBundle(props.credits),
    }),
    ...(props?.amtMedicalExpAdjustmentsItems && {
      amtMedicalExpAdjustmentsItems: ImRecord.isRecord(props.amtMedicalExpAdjustmentsItems)
        ? props.amtMedicalExpAdjustmentsItems
        : mkValueBundle(props.amtMedicalExpAdjustmentsItems),
    }),
    ...(props?.niiagiAdj && {
      niiagiAdj: ImRecord.isRecord(props.niiagiAdj) ? props.niiagiAdj : mkValueBundle(props.niiagiAdj),
    }),
  });

export type WithholdingsAndSummaryProps = {
  withholdingsSelf?: ValueBundle | ValueBundleProps;
  sNextPayDateSelf?: string;
  sPayPeriodSelf?: string;
  withholdingPerPayPeriodSelf?: ValueBundle | ValueBundleProps;
  withholdingsSpouse?: ValueBundle | ValueBundleProps;
  sNextPayDateSpouse?: string;
  sPayPeriodSpouse?: string;
  withholdingPerPayPeriodSpouse?: ValueBundle | ValueBundleProps;
  otherWithholdingsSelf?: ValueBundle | ValueBundleProps;
  otherWithholdingsSpouse?: ValueBundle | ValueBundleProps;
};
export type WithholdingsAndSummary = RecordOf<WithholdingsAndSummaryProps>;
export const WithholdingsAndSummaryRecord = ImRecord<WithholdingsAndSummaryProps>({
  withholdingsSelf: undefined,
  sNextPayDateSelf: undefined,
  sPayPeriodSelf: undefined,
  withholdingPerPayPeriodSelf: undefined,
  withholdingsSpouse: undefined,
  sNextPayDateSpouse: undefined,
  sPayPeriodSpouse: undefined,
  withholdingPerPayPeriodSpouse: undefined,
  otherWithholdingsSelf: undefined,
  otherWithholdingsSpouse: undefined,
});
export const mkWithholdingsAndSummary = (props?: WithholdingsAndSummaryProps): WithholdingsAndSummary =>
  WithholdingsAndSummaryRecord({
    ...props,
    ...(props?.withholdingsSelf && {
      withholdingsSelf: ImRecord.isRecord(props.withholdingsSelf)
        ? props.withholdingsSelf
        : mkValueBundle(props.withholdingsSelf),
    }),
    ...(props?.withholdingsSpouse && {
      withholdingsSpouse: ImRecord.isRecord(props.withholdingsSpouse)
        ? props.withholdingsSpouse
        : mkValueBundle(props.withholdingsSpouse),
    }),
    ...(props?.withholdingPerPayPeriodSelf && {
      withholdingPerPayPeriodSelf: ImRecord.isRecord(props.withholdingPerPayPeriodSelf)
        ? props.withholdingPerPayPeriodSelf
        : mkValueBundle(props.withholdingPerPayPeriodSelf),
    }),
    ...(props?.otherWithholdingsSelf && {
      otherWithholdingsSelf: ImRecord.isRecord(props.otherWithholdingsSelf)
        ? props.otherWithholdingsSelf
        : mkValueBundle(props.otherWithholdingsSelf),
    }),
    ...(props?.otherWithholdingsSpouse && {
      otherWithholdingsSpouse: ImRecord.isRecord(props.otherWithholdingsSpouse)
        ? props.otherWithholdingsSpouse
        : mkValueBundle(props.otherWithholdingsSpouse),
    }),
  });

export type ProfileProps = {
  stModelId?: string;
  numberOfDependents?: number;
  q1SocDec?: number;
  q2SocDec?: number;
  q3SocDec?: number;
  q4SocDec?: number;
  q1Medicare?: number;
  q2Medicare?: number;
  q3Medicare?: number;
  q4Medicare?: number;
  q1FederalIncomeTax?: number;
  q2FederalIncomeTax?: number;
  q3FederalIncomeTax?: number;
  q4FederalIncomeTax?: number;
  q1EstimatedTax?: number;
  q2EstimatedTax?: number;
  q3EstimatedTax?: number;
  q4EstimatedTax?: number;
  q1PaymentLinkedTxnId?: string;
  q2PaymentLinkedTxnId?: string;
  q3PaymentLinkedTxnId?: string;
  q4PaymentLinkedTxnId?: string;
  q1BizIncome?: ValueBundle | ValueBundleProps;
  q2BizIncome?: ValueBundle | ValueBundleProps;
  q3BizIncome?: ValueBundle | ValueBundleProps;
  q4BizIncome?: ValueBundle | ValueBundleProps;
  q1BizDeductions?: ValueBundle | ValueBundleProps;
  q2BizDeductions?: ValueBundle | ValueBundleProps;
  q3BizDeductions?: ValueBundle | ValueBundleProps;
  q4BizDeductions?: ValueBundle | ValueBundleProps;
};
export type Profile = RecordOf<ProfileProps>;
export const ProfileRecord = ImRecord<ProfileProps>({
  stModelId: undefined,
  numberOfDependents: undefined,
  q1SocDec: undefined,
  q2SocDec: undefined,
  q3SocDec: undefined,
  q4SocDec: undefined,
  q1Medicare: undefined,
  q2Medicare: undefined,
  q3Medicare: undefined,
  q4Medicare: undefined,
  q1FederalIncomeTax: undefined,
  q2FederalIncomeTax: undefined,
  q3FederalIncomeTax: undefined,
  q4FederalIncomeTax: undefined,
  q1EstimatedTax: undefined,
  q2EstimatedTax: undefined,
  q3EstimatedTax: undefined,
  q4EstimatedTax: undefined,
  q1PaymentLinkedTxnId: undefined,
  q2PaymentLinkedTxnId: undefined,
  q3PaymentLinkedTxnId: undefined,
  q4PaymentLinkedTxnId: undefined,
  q1BizIncome: undefined,
  q2BizIncome: undefined,
  q3BizIncome: undefined,
  q4BizIncome: undefined,
  q1BizDeductions: undefined,
  q2BizDeductions: undefined,
  q3BizDeductions: undefined,
  q4BizDeductions: undefined,
});
export const mkProfile = (props?: ProfileProps): Profile =>
  ProfileRecord({
    ...props,
    ...(props?.q1BizIncome && {
      q1BizIncome: ImRecord.isRecord(props.q1BizIncome) ? props.q1BizIncome : mkValueBundle(props.q1BizIncome),
    }),
    ...(props?.q2BizIncome && {
      q2BizIncome: ImRecord.isRecord(props.q2BizIncome) ? props.q2BizIncome : mkValueBundle(props.q2BizIncome),
    }),
    ...(props?.q3BizIncome && {
      q3BizIncome: ImRecord.isRecord(props.q3BizIncome) ? props.q3BizIncome : mkValueBundle(props.q3BizIncome),
    }),
    ...(props?.q4BizIncome && {
      q4BizIncome: ImRecord.isRecord(props.q4BizIncome) ? props.q4BizIncome : mkValueBundle(props.q4BizIncome),
    }),
    ...(props?.q1BizDeductions && {
      q1BizDeductions: ImRecord.isRecord(props.q1BizDeductions)
        ? props.q1BizDeductions
        : mkValueBundle(props.q1BizDeductions),
    }),
    ...(props?.q2BizDeductions && {
      q2BizDeductions: ImRecord.isRecord(props.q2BizDeductions)
        ? props.q2BizDeductions
        : mkValueBundle(props.q2BizDeductions),
    }),
    ...(props?.q3BizDeductions && {
      q3BizDeductions: ImRecord.isRecord(props.q3BizDeductions)
        ? props.q3BizDeductions
        : mkValueBundle(props.q3BizDeductions),
    }),
    ...(props?.q4BizDeductions && {
      q4BizDeductions: ImRecord.isRecord(props.q4BizDeductions)
        ? props.q4BizDeductions
        : mkValueBundle(props.q4BizDeductions),
    }),
  });

export type TaxInputProps = {
  id?: string;
  isDeleted?: boolean;
  clientId?: string;
  createdAt?: string;
  modifiedAt?: string;

  profile?: Profile | ProfileProps;

  // calc params
  year?: number;
  filingStatus?: FilingStatus;
  wages?: Wages | WagesProps;
  taxPayments?: TaxPayments | TaxPaymentsProps;
  divInterest?: DivInterest | DivInterestProps;
  capitalGains?: CapitalGains | CapitalGainsProps;
  businessIncome?: BusinessIncome | BusinessIncomeProps;
  otherIncome?: OtherIncome | OtherIncomeProps;
  scheduleE?: ScheduleE | ScheduleEProps;
  scheduleF?: ScheduleF | ScheduleFProps;
  adjustments?: Adjustments | AdjustmentsProps;
  scheduleADeductions?: ScheduleADeductions | ScheduleADeductionsProps;
  stateAndLocalIncomeTax?: StateAndLocalIncomeTax | StateAndLocalIncomeTaxProps;
  taxesAndCredits?: TaxesAndCredits | TaxesAndCreditsProps;
  withholdingsAndSummary?: WithholdingsAndSummary | WithholdingsAndSummaryProps;
};
export type TaxInput = RecordOf<TaxInputProps>;
export const TaxInputRecord = ImRecord<TaxInputProps>({
  id: undefined,
  isDeleted: undefined,
  clientId: undefined,
  createdAt: undefined,
  modifiedAt: undefined,

  year: undefined,

  filingStatus: undefined,
  wages: undefined,
  taxPayments: undefined,
  divInterest: undefined,
  capitalGains: undefined,
  businessIncome: undefined,
  otherIncome: undefined,
  scheduleE: undefined,
  scheduleF: undefined,
  adjustments: undefined,
  scheduleADeductions: undefined,
  stateAndLocalIncomeTax: undefined,
  taxesAndCredits: undefined,
  withholdingsAndSummary: undefined,
  profile: undefined,
});
export const mkTaxInput = (props?: TaxInputProps): TaxInput =>
  TaxInputRecord({
    ...props,
    ...(props?.wages && { wages: ImRecord.isRecord(props.wages) ? props.wages : mkWages(props.wages) }),
    ...(props?.taxPayments && {
      taxPayments: ImRecord.isRecord(props.taxPayments) ? props.taxPayments : mkTaxPayments(props.taxPayments),
    }),
    ...(props?.divInterest && {
      divInterest: ImRecord.isRecord(props.divInterest) ? props.divInterest : mkDivInterest(props.divInterest),
    }),
    ...(props?.capitalGains && {
      capitalGains: ImRecord.isRecord(props.capitalGains) ? props.capitalGains : mkCapitalGains(props.capitalGains),
    }),
    ...(props?.businessIncome && {
      businessIncome: ImRecord.isRecord(props.businessIncome)
        ? props.businessIncome
        : mkBusinessIncome(props.businessIncome),
    }),
    ...(props?.otherIncome && {
      otherIncome: ImRecord.isRecord(props.otherIncome) ? props.otherIncome : mkOtherIncome(props.otherIncome),
    }),
    ...(props?.scheduleE && {
      scheduleE: ImRecord.isRecord(props.scheduleE) ? props.scheduleE : mkScheduleE(props.scheduleE),
    }),
    ...(props?.scheduleF && {
      scheduleF: ImRecord.isRecord(props.scheduleF) ? props.scheduleF : mkScheduleF(props.scheduleF),
    }),
    ...(props?.adjustments && {
      adjustments: ImRecord.isRecord(props.adjustments) ? props.adjustments : mkAdjustments(props.adjustments),
    }),
    ...(props?.scheduleADeductions && {
      scheduleADeductions: ImRecord.isRecord(props.scheduleADeductions)
        ? props.scheduleADeductions
        : mkScheduleADeductions(props.scheduleADeductions),
    }),
    ...(props?.stateAndLocalIncomeTax && {
      stateAndLocalIncomeTax: ImRecord.isRecord(props.stateAndLocalIncomeTax)
        ? props.stateAndLocalIncomeTax
        : mkStateAndLocalIncomeTax(props.stateAndLocalIncomeTax),
    }),
    ...(props?.taxesAndCredits && {
      taxesAndCredits: ImRecord.isRecord(props.taxesAndCredits)
        ? props.taxesAndCredits
        : mkTaxesAndCredits(props.taxesAndCredits),
    }),
    ...(props?.withholdingsAndSummary && {
      withholdingsAndSummary: ImRecord.isRecord(props.withholdingsAndSummary)
        ? props.withholdingsAndSummary
        : mkWithholdingsAndSummary(props.withholdingsAndSummary),
    }),
    ...(props?.profile && {
      profile: ImRecord.isRecord(props.profile) ? props.profile : mkProfile(props.profile),
    }),
  });

export type F1040CalcProps = {
  wagesAndSalaries?: number;
  interestAndDividendIncome?: number;
  businessIncome?: number;
  capitalGainsAndLosses?: number;
  otherIncome?: number;
  totalIncome?: number;
  adjustmentsToIncome?: number;
  adjustedGrossIncome?: number;
  deductions?: number;
  exemptions?: number;
  taxableIncome?: number;
  incomeTax?: number;
  otherTaxesAndCredit?: number;
  totalTax?: number;
  marginalRateOnSalary?: number;
  marginalRateOnCapitalGains?: number;
  marginalRateOnBusinessIncome?: number;
  averageRate?: number;
  actualAndProjectedWithholding?: number;
  actualAndProjectedEstimatedTaxPayments?: number;
  remainingTaxDue?: number;
};
export type F1040Calc = RecordOf<F1040CalcProps>;
export const mkF1040Calc = ImRecord<F1040CalcProps>({
  wagesAndSalaries: undefined,
  interestAndDividendIncome: undefined,
  businessIncome: undefined,
  capitalGainsAndLosses: undefined,
  otherIncome: undefined,
  totalIncome: undefined,
  adjustmentsToIncome: undefined,
  adjustedGrossIncome: undefined,
  deductions: undefined,
  exemptions: undefined,
  taxableIncome: undefined,
  incomeTax: undefined,
  otherTaxesAndCredit: undefined,
  totalTax: undefined,
  marginalRateOnSalary: undefined,
  marginalRateOnCapitalGains: undefined,
  marginalRateOnBusinessIncome: undefined,
  averageRate: undefined,
  actualAndProjectedWithholding: undefined,
  actualAndProjectedEstimatedTaxPayments: undefined,
  remainingTaxDue: undefined,
});

export type WagesCalcProps = {
  selfWagesTotal?: number;
  spouseWagesTotal?: number;
  wagesTotal?: number;
};
export type WagesCalc = RecordOf<WagesCalcProps>;
export const mkWagesCalc = ImRecord<WagesCalcProps>({
  selfWagesTotal: undefined,
  spouseWagesTotal: undefined,
  wagesTotal: undefined,
});

export type BusinessIncomeCalcProps = {
  grossMarginSelf?: number;
  grossMarginSpouse?: number;
  deductibleMealsAndEntertainmentSelf?: number;
  deductibleMealsAndEntertainmentSpouse?: number;
  totalExpensesSelf?: number;
  totalExpensesSpouse?: number;
  businessIncomeSelf?: number;
  businessIncomeSpouse?: number;
};
export type BusinessIncomeCalc = RecordOf<BusinessIncomeCalcProps>;
export const mkBusinessIncomeCalc = ImRecord<BusinessIncomeCalcProps>({
  grossMarginSelf: undefined,
  grossMarginSpouse: undefined,
  deductibleMealsAndEntertainmentSelf: undefined,
  deductibleMealsAndEntertainmentSpouse: undefined,
  totalExpensesSelf: undefined,
  totalExpensesSpouse: undefined,
  businessIncomeSelf: undefined,
  businessIncomeSpouse: undefined,
});

export type CapitalGainsCalcProps = {
  shortTermGainsAltMin?: number;
  unrecapturedDepreciationGainsAltMin?: number;
  longTerm28PctGainsAltMin?: number;
  longTermGainsAltMin?: number;
  netTaxableGains?: number;
};
export type CapitalGainsCalc = RecordOf<CapitalGainsCalcProps>;
export const mkCapitalGainsCalc = ImRecord<CapitalGainsCalcProps>({
  shortTermGainsAltMin: undefined,
  unrecapturedDepreciationGainsAltMin: undefined,
  longTerm28PctGainsAltMin: undefined,
  longTermGainsAltMin: undefined,
  netTaxableGains: undefined,
});

export type ScheduleECalcProps = {
  totalIncome?: number;
  netIncome?: number;
};
export type ScheduleECalc = RecordOf<ScheduleECalcProps>;
export const mkScheduleECalc = ImRecord<ScheduleECalcProps>({
  totalIncome: undefined,
  netIncome: undefined,
});
export type ScheduleFCalcProps = {
  totalIncome?: number;
  netIncome?: number;
};
export type ScheduleFCalc = RecordOf<ScheduleFCalcProps>;
export const mkScheduleFCalc = ImRecord<ScheduleFCalcProps>({
  totalIncome: undefined,
  netIncome: undefined,
});
export type OtherIncomeCalcProps = {
  scheduleEIncome?: number;
  scheduleFIncome?: number;
  totalOtherIncome?: number;
};
export type OtherIncomeCalc = RecordOf<OtherIncomeCalcProps>;
export const mkOtherIncomeCalc = ImRecord<OtherIncomeCalcProps>({
  scheduleEIncome: undefined,
  scheduleFIncome: undefined,
  totalOtherIncome: undefined,
});
export type AdjustmentsCalcProps = {
  halfOfSelfEmploymentTax?: number;
  totalAdjustments?: number;
};
export type AdjustmentsCalc = RecordOf<AdjustmentsCalcProps>;
export const mkAdjustmentsCalc = ImRecord<AdjustmentsCalcProps>({
  halfOfSelfEmploymentTax: undefined,
  totalAdjustments: undefined,
});
export type StateAndLocalIncomeTaxCalcProps = {
  projectedFutureWithholdingSelf?: number;
  projectedTotalWithholdingSelf?: number;
  projectedFutureWithholdingSpouse?: number;
  projectedTotalWithholdingSpouse?: number;
  projectedTotalWithholdingSelfAndSpouse?: number;
  totalEstTaxPaymentsAndWithholdingFullYear?: number;
};
export type StateAndLocalIncomeTaxCalc = RecordOf<StateAndLocalIncomeTaxCalcProps>;
export const mkStateAndLocalIncomeTaxCalc = ImRecord<StateAndLocalIncomeTaxCalcProps>({
  projectedFutureWithholdingSelf: undefined,
  projectedTotalWithholdingSelf: undefined,
  projectedFutureWithholdingSpouse: undefined,
  projectedTotalWithholdingSpouse: undefined,
  projectedTotalWithholdingSelfAndSpouse: undefined,
  totalEstTaxPaymentsAndWithholdingFullYear: undefined,
});
export type ScheduleADeductionsCalcProps = {
  allowedMedical?: number;
  stateAndLocalTax?: number;
  stateLocalTaxAndOtherLimitations?: number;
  totalItemizedDeductions?: number;
  standardDeduction?: number;
  largerOfItemizedOrStandardDeduction?: number;
};
export type ScheduleADeductionsCalc = RecordOf<ScheduleADeductionsCalcProps>;
export const mkScheduleADeductionsCalc = ImRecord<ScheduleADeductionsCalcProps>({
  allowedMedical: undefined,
  stateAndLocalTax: undefined,
  stateLocalTaxAndOtherLimitations: undefined,
  totalItemizedDeductions: undefined,
  standardDeduction: undefined,
  largerOfItemizedOrStandardDeduction: undefined,
});
export type ExemptionsCalcProps = {
  exemptionsSelfAndSpouse?: number;
  totalExemptions?: number;
  exemptionDeductions?: number;
};
export type ExemptionsCalc = RecordOf<ExemptionsCalcProps>;
export const mkExemptionsCalc = ImRecord<ExemptionsCalcProps>({
  exemptionsSelfAndSpouse: undefined,
  totalExemptions: undefined,
  exemptionDeductions: undefined,
});
export type TaxesAndCreditsCalcProps = {
  seScheduleCAndFIncomeSelf?: number;
  seScheduleCAAndFIncomeSpouse?: number;
  seForm1040WagesSelf?: number;
  seForm1040WagesSpouse?: number;
  seTaxSelf?: number;
  seTaxSpouse?: number;
  seTaxTotal?: number;
  amtExcludedDecutions?: number;
  amtAMTTaxableIncome?: number;
  amtRegularTaxBeforeCredits?: number;
  amtTentativeAMT?: number;
  amtAMT?: number;
  netInvIncomeTax?: number;
  totalOtherTaxesAndUsableCredits?: number;
};
export type TaxesAndCreditsCalc = RecordOf<TaxesAndCreditsCalcProps>;
export const mkTaxesAndCreditsCalc = ImRecord<TaxesAndCreditsCalcProps>({
  seScheduleCAndFIncomeSelf: undefined,
  seScheduleCAAndFIncomeSpouse: undefined,
  seForm1040WagesSelf: undefined,
  seForm1040WagesSpouse: undefined,
  seTaxSelf: undefined,
  seTaxSpouse: undefined,
  seTaxTotal: undefined,
  amtExcludedDecutions: undefined,
  amtAMTTaxableIncome: undefined,
  amtRegularTaxBeforeCredits: undefined,
  amtTentativeAMT: undefined,
  amtAMT: undefined,
  netInvIncomeTax: undefined,
  totalOtherTaxesAndUsableCredits: undefined,
});
export type EstTaxCalcProps = {
  suggestedApril?: number;
  suggestedJune?: number;
  suggestedSeptember?: number;
  suggestedJanuary?: number;
};
export type EstTaxCalc = RecordOf<EstTaxCalcProps>;
export const mkEstTaxCalc = ImRecord<EstTaxCalcProps>({
  suggestedApril: undefined,
  suggestedJune: undefined,
  suggestedSeptember: undefined,
  suggestedJanuary: undefined,
});
export type TaxPaymentsCalcProps = {
  totalEstimatedTaxPaymentsAndRefundsApplied?: number;
  estimatedTaxesPaidOrPlanned?: number;
  estTaxSafeHarborPercentagePaid?: number;
};
export type TaxPaymentsCalc = RecordOf<TaxPaymentsCalcProps>;
export const mkTaxPaymentsCalc = ImRecord<TaxPaymentsCalcProps>({
  totalEstimatedTaxPaymentsAndRefundsApplied: undefined,
  estimatedTaxesPaidOrPlanned: undefined,
  estTaxSafeHarborPercentagePaid: undefined,
});
export type WithholdingsAndSummaryCalcProps = {
  projectedFutureWithholdingsSelf?: number;
  projectedTotalWithholdingsSelf?: number;
  projectedFutureWithholdingsSpouse?: number;
  projectedTotalWithholdingsSpouse?: number;
  totalWithholdingsFullYearAndOther?: number;
  projectedTax?: number;
  withholding?: number;
  estimatedTaxPayments?: number;
  remainingTaxDue?: number;
};
export type WithholdingsAndSummaryCalc = RecordOf<WithholdingsAndSummaryCalcProps>;
export const mkWithholdingsAndSummaryCalc = ImRecord<WithholdingsAndSummaryCalcProps>({
  projectedFutureWithholdingsSelf: undefined,
  projectedTotalWithholdingsSelf: undefined,
  projectedFutureWithholdingsSpouse: undefined,
  projectedTotalWithholdingsSpouse: undefined,
  totalWithholdingsFullYearAndOther: undefined,
  projectedTax: undefined,
  withholding: undefined,
  estimatedTaxPayments: undefined,
  remainingTaxDue: undefined,
});

export type TaxOutputProps = {
  stModelId?: string;
  year?: number;

  f1040Calc?: F1040Calc | F1040CalcProps;
  wagesCalc?: WagesCalc | WagesCalcProps;
  businessIncomeCalc?: BusinessIncomeCalc | BusinessIncomeCalcProps;
  capitalGainsCalc?: CapitalGainsCalc | CapitalGainsCalcProps;
  scheduleECalc?: ScheduleECalc | ScheduleECalcProps;
  scheduleFCalc?: ScheduleFCalc | ScheduleFCalcProps;
  otherIncomeCalc?: OtherIncomeCalc | OtherIncomeCalcProps;
  adjustmentsCalc?: AdjustmentsCalc | AdjustmentsCalcProps;
  stateAndLocalIncomeTaxCalc?: StateAndLocalIncomeTaxCalc | StateAndLocalIncomeTaxCalcProps;
  scheduleADeductionsCalc?: ScheduleADeductionsCalc | ScheduleADeductionsCalcProps;
  exemptionsCalc?: ExemptionsCalc | ExemptionsCalcProps;
  taxesAndCreditsCalc?: TaxesAndCreditsCalc | TaxesAndCreditsCalcProps;
  estTaxCalc?: EstTaxCalc | EstTaxCalcProps;
  taxPaymentsCalc?: TaxPaymentsCalc | TaxPaymentsCalcProps;
  withholdingsAndSummaryCalc?: WithholdingsAndSummaryCalc | WithholdingsAndSummaryCalcProps;
};
export type TaxOutput = RecordOf<TaxOutputProps>;
export const TaxOutputRecord = ImRecord<TaxOutputProps>({
  stModelId: undefined,
  year: undefined,
  f1040Calc: undefined,
  wagesCalc: undefined,
  businessIncomeCalc: undefined,
  capitalGainsCalc: undefined,
  scheduleECalc: undefined,
  scheduleFCalc: undefined,
  otherIncomeCalc: undefined,
  adjustmentsCalc: undefined,
  stateAndLocalIncomeTaxCalc: undefined,
  scheduleADeductionsCalc: undefined,
  exemptionsCalc: undefined,
  taxesAndCreditsCalc: undefined,
  estTaxCalc: undefined,
  taxPaymentsCalc: undefined,
  withholdingsAndSummaryCalc: undefined,
});
export const mkTaxOutput = (props?: TaxOutputProps): TaxOutput =>
  TaxOutputRecord({
    ...props,
    ...(props?.f1040Calc && {
      f1040Calc: ImRecord.isRecord(props.f1040Calc) ? props.f1040Calc : mkF1040Calc(props.f1040Calc),
    }),
    ...(props?.wagesCalc && {
      wagesCalc: ImRecord.isRecord(props.wagesCalc) ? props.wagesCalc : mkWagesCalc(props.wagesCalc),
    }),
    ...(props?.businessIncomeCalc && {
      businessIncomeCalc: ImRecord.isRecord(props.businessIncomeCalc)
        ? props.businessIncomeCalc
        : mkBusinessIncomeCalc(props.businessIncomeCalc),
    }),
    ...(props?.capitalGainsCalc && {
      capitalGainsCalc: ImRecord.isRecord(props.capitalGainsCalc)
        ? props.capitalGainsCalc
        : mkCapitalGainsCalc(props.capitalGainsCalc),
    }),
    ...(props?.scheduleECalc && {
      scheduleECalc: ImRecord.isRecord(props.scheduleECalc)
        ? props.scheduleECalc
        : mkScheduleECalc(props.scheduleECalc),
    }),
    ...(props?.scheduleFCalc && {
      scheduleFCalc: ImRecord.isRecord(props.scheduleFCalc)
        ? props.scheduleFCalc
        : mkScheduleFCalc(props.scheduleFCalc),
    }),
    ...(props?.otherIncomeCalc && {
      otherIncomeCalc: ImRecord.isRecord(props.otherIncomeCalc)
        ? props.otherIncomeCalc
        : mkOtherIncomeCalc(props.otherIncomeCalc),
    }),
    ...(props?.adjustmentsCalc && {
      adjustmentsCalc: ImRecord.isRecord(props.adjustmentsCalc)
        ? props.adjustmentsCalc
        : mkAdjustmentsCalc(props.adjustmentsCalc),
    }),
    ...(props?.stateAndLocalIncomeTaxCalc && {
      stateAndLocalIncomeTaxCalc: ImRecord.isRecord(props.stateAndLocalIncomeTaxCalc)
        ? props.stateAndLocalIncomeTaxCalc
        : mkStateAndLocalIncomeTaxCalc(props.stateAndLocalIncomeTaxCalc),
    }),
    ...(props?.scheduleADeductionsCalc && {
      scheduleADeductionsCalc: ImRecord.isRecord(props.scheduleADeductionsCalc)
        ? props.scheduleADeductionsCalc
        : mkScheduleADeductionsCalc(props.scheduleADeductionsCalc),
    }),
    ...(props?.exemptionsCalc && {
      exemptionsCalc: ImRecord.isRecord(props.exemptionsCalc)
        ? props.exemptionsCalc
        : mkExemptionsCalc(props.exemptionsCalc),
    }),
    ...(props?.taxesAndCreditsCalc && {
      taxesAndCreditsCalc: ImRecord.isRecord(props.taxesAndCreditsCalc)
        ? props.taxesAndCreditsCalc
        : mkTaxesAndCreditsCalc(props.taxesAndCreditsCalc),
    }),
    ...(props?.estTaxCalc && {
      estTaxCalc: ImRecord.isRecord(props.estTaxCalc) ? props.estTaxCalc : mkEstTaxCalc(props.estTaxCalc),
    }),
    ...(props?.taxPaymentsCalc && {
      taxPaymentsCalc: ImRecord.isRecord(props.taxPaymentsCalc)
        ? props.taxPaymentsCalc
        : mkTaxPaymentsCalc(props.taxPaymentsCalc),
    }),
    ...(props?.withholdingsAndSummaryCalc && {
      withholdingsAndSummaryCalc: ImRecord.isRecord(props.withholdingsAndSummaryCalc)
        ? props.withholdingsAndSummaryCalc
        : mkWithholdingsAndSummaryCalc(props.withholdingsAndSummaryCalc),
    }),
  });

export type TaxProps = {
  id?: string;
  isDeleted?: boolean;
  clientId?: string;
  createdAt?: string;
  modifiedAt?: string;
  version?: string;
  input?: TaxInput | TaxInputProps;
  output?: TaxOutput | TaxOutputProps;
};
export type Tax = RecordOf<TaxProps>;
export const TaxRecord = ImRecord<TaxProps>({
  id: undefined,
  isDeleted: undefined,
  clientId: undefined,
  createdAt: undefined,
  modifiedAt: undefined,
  version: undefined,
  input: undefined,
  output: undefined,
});
export const mkTax = (props: TaxProps): Tax =>
  TaxRecord({
    ...props,
    input: ImRecord.isRecord(props.input) ? props.input : mkTaxInput(props.input),
    output: !ImRecord.isRecord(props.output) ? mkTaxOutput(props.output) : props.output,
  });

export type PaymentStatus = 'PAID' | 'PENDING' | 'OVERDUE' | 'DUE_TODAY' | 'SKIPPED' | 'UPCOMING' | '';
export type TaxQuarterProps = {
  transactions: ImList<transactionsTypes.TransactionRecord> | transactionsTypes.TransactionRecord[];
  paymentStatus: PaymentStatus;
  dueDate: string;
  amountEstimated: number;
  amountPaid: number;
  startDate: string;
  endDate: string;
  id: 'q1' | 'q2' | 'q3' | 'q4';
  label: 'Q1' | 'Q2' | 'Q3' | 'Q4';
};
export type TaxQuarter = RecordOf<TaxQuarterProps>;
export const mkTaxQuarter = ImRecord<Partial<TaxQuarterProps>>({
  transactions: ImList(),
  paymentStatus: '',
  dueDate: undefined,
  amountEstimated: undefined,
  amountPaid: undefined,
  startDate: undefined,
  endDate: undefined,
  id: undefined,
  label: undefined,
});

export type TaxPlanStats = {
  amountPaid: number;
  amountEstimated: number;
  transactionsPaid: transactionsTypes.TransactionRecord[];
  transactionsUpcoming: transactionsTypes.TransactionRecord[];
};

export type TaxesStoreProps = QcsSyncResourceStoreProps<TaxProps> & {
  isProcessing: boolean;
};
declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [TAXES_REDUCER_KEY]: QcsSyncResourceStore<TaxProps, TaxesStoreProps>;
  }
}
